import { useRef, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Globe,
  Upload,
  Loader2,
  CheckCircle2,
  FileText,
  File as FileIcon,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";

const ACCEPTED_TYPES = ".pdf,.docx,.doc,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword";
const MAX_FILE_MB = 15;

type ImportPanelProps = { onSwitchTab: (tab: string) => void };

export function ImportPanel({ onSwitchTab }: ImportPanelProps) {
  const utils = trpc.useUtils();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [url, setUrl] = useState("");
  const [urlTitle, setUrlTitle] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileTitle, setFileTitle] = useState("");

  const importUrlMut = trpc.intelligence.importFromUrl.useMutation({
    onSuccess: () => {
      utils.knowledgeBase.list.invalidate();
      toast.success("URL importada! Texto extraído e enviado para a base.");
      setUrl("");
      setUrlTitle("");
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const importFileMut = trpc.intelligence.importFromFile.useMutation({
    onSuccess: () => {
      utils.knowledgeBase.list.invalidate();
      toast.success("Arquivo importado! Texto extraído e enviado para a base.");
      setSelectedFile(null);
      setFileTitle("");
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    if (!file) {
      setSelectedFile(null);
      return;
    }
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      toast.error(`Arquivo excede ${MAX_FILE_MB}MB`);
      e.target.value = "";
      return;
    }
    setSelectedFile(file);
    if (!fileTitle.trim()) {
      const baseName = file.name.replace(/\.[^.]+$/, "");
      setFileTitle(baseName.slice(0, 100));
    }
  }

  /**
   * FileReader.readAsDataURL é nativo e roda em background, evitando o
   * O(n²) da concatenação de string anterior. Strip do prefixo
   * `data:<mime>;base64,` é O(1) com indexOf.
   */
  function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error ?? new Error("Falha ao ler o arquivo"));
      reader.onload = () => {
        const result = reader.result;
        if (typeof result !== "string") {
          reject(new Error("Resultado do FileReader em formato inesperado"));
          return;
        }
        const commaIdx = result.indexOf(",");
        resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
      };
      reader.readAsDataURL(file);
    });
  }

  async function handleSubmitFile() {
    if (!selectedFile) return;
    let base64: string;
    try {
      base64 = await fileToBase64(selectedFile);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast.error(`Falha ao ler arquivo: ${message}`);
      return;
    }
    importFileMut.mutate({
      fileName: selectedFile.name,
      mimeType: selectedFile.type || "application/octet-stream",
      contentBase64: base64,
      title: fileTitle.trim() || undefined,
    });
  }

  function handleSubmitUrl() {
    if (!url.trim()) return;
    importUrlMut.mutate({
      url: url.trim(),
      title: urlTitle.trim() || undefined,
    });
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Importar conhecimento</h2>
        <p className="text-sm text-muted-foreground">
          Suba arquivos ou cole o link do seu site/proposta. O sistema extrai o texto, divide em chunks e a IA passa a usar como base de conhecimento.
        </p>
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        <Card className="p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-indigo-600" />
            <h3 className="font-semibold">Importar via URL</h3>
          </div>
          <p className="text-xs text-muted-foreground">
            Funciona com sites estáticos e blogs. Sites que dependem de JavaScript (SPAs) podem retornar texto incompleto.
          </p>

          <div>
            <Label>URL</Label>
            <Input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="meusite.com.br ou https://meusite.com/pagina"
              className="mt-1"
              disabled={importUrlMut.isPending}
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Pode colar com ou sem <code>https://</code>. Domínios sem protocolo recebem <code>https://</code> automaticamente.
            </p>
          </div>

          <div>
            <Label>Título (opcional)</Label>
            <Input
              value={urlTitle}
              onChange={(e) => setUrlTitle(e.target.value)}
              placeholder="Deixe vazio para usar o título da página"
              className="mt-1"
              disabled={importUrlMut.isPending}
            />
          </div>

          <Button onClick={handleSubmitUrl} disabled={!url.trim() || importUrlMut.isPending} className="w-full">
            {importUrlMut.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Buscando...
              </>
            ) : (
              <>
                <Globe className="w-4 h-4 mr-2" /> Buscar e importar
              </>
            )}
          </Button>

          {importUrlMut.isSuccess && (
            <div className="flex items-start gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">
              <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Importado com sucesso.</p>
                <button
                  type="button"
                  className="underline text-emerald-700"
                  onClick={() => onSwitchTab("documents")}
                >
                  Ver na aba Documentos
                </button>
              </div>
            </div>
          )}
          {importUrlMut.isError && (
            <div className="flex items-start gap-2 text-xs text-destructive bg-destructive/5 border border-destructive/30 rounded p-2">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <p>{importUrlMut.error.message}</p>
            </div>
          )}
        </Card>

        <Card className="p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-indigo-600" />
            <h3 className="font-semibold">Importar arquivo (PDF / DOCX)</h3>
          </div>
          <p className="text-xs text-muted-foreground">
            Suba propostas, playbooks de venda, e-mails antigos, manuais. Limite de {MAX_FILE_MB}MB. PDFs apenas com texto (não digitalizações).
          </p>

          <div>
            <Label>Arquivo</Label>
            <div className="mt-1">
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_TYPES}
                onChange={handleFileChange}
                disabled={importFileMut.isPending}
                className="block w-full text-xs file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-primary/10 file:text-primary file:cursor-pointer hover:file:bg-primary/20"
              />
            </div>
            {selectedFile && (
              <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1">
                {selectedFile.name.endsWith(".pdf") ? <FileText className="w-3 h-3" /> : <FileIcon className="w-3 h-3" />}
                {(selectedFile.size / 1024).toFixed(1)} KB
              </p>
            )}
          </div>

          <div>
            <Label>Título (opcional)</Label>
            <Input
              value={fileTitle}
              onChange={(e) => setFileTitle(e.target.value)}
              placeholder="Deixe vazio para usar o nome do arquivo"
              className="mt-1"
              disabled={importFileMut.isPending}
            />
          </div>

          <Button onClick={handleSubmitFile} disabled={!selectedFile || importFileMut.isPending} className="w-full">
            {importFileMut.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Extraindo texto...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" /> Extrair e importar
              </>
            )}
          </Button>

          {importFileMut.isSuccess && (
            <div className="flex items-start gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">
              <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Importado com sucesso.</p>
                <button
                  type="button"
                  className="underline text-emerald-700"
                  onClick={() => onSwitchTab("documents")}
                >
                  Ver na aba Documentos
                </button>
              </div>
            </div>
          )}
          {importFileMut.isError && (
            <div className="flex items-start gap-2 text-xs text-destructive bg-destructive/5 border border-destructive/30 rounded p-2">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <p>{importFileMut.error.message}</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
